/*
 * MENUS DE COULEURS REPLIÉS PAR FAMILLE (10/08/2026).
 *
 * « Il y a trop de couleurs enregistrées et ça devient du n'importe quoi. »
 *
 * Constat mesuré en production : 55 couleurs pour une vingtaine de familles
 * réelles — rose comptait 8 nuances, vert 8, bleu 8, noir 6. Le menu déroulant
 * était devenu une liste qu'on parcourt au lieu d'un choix qu'on fait.
 *
 * On n'a RIEN fusionné : « Rouge Bordeau » (55 variantes) ne se vend pas comme
 * « Rouge ». Chaque couleur est simplement rattachée à une famille, et les menus
 * s'ouvrent sur 15 groupes que l'on déplie.
 *
 * CE FICHIER EST PARTAGÉ par les 14 écrans qui listent des couleurs : le jour
 * où une famille change, elle change partout. C'est aussi ce qui évite d'aller
 * réécrire la même boucle quatorze fois.
 *
 * FAIL-OPEN, deux fois plutôt qu'une :
 *   - une couleur SANS famille n'est jamais perdue : elle se range sous
 *     « Sans famille », visible et sélectionnable ;
 *   - si l'API ne renvoie pas encore le champ `famille` (vieux serveur, vieille
 *     APK), la liste retombe d'elle-même sur un menu plat — l'écran marche,
 *     simplement sans groupes.
 */

const SANS_FAMILLE = 'Sans famille';

/**
 * Transforme la liste brute des couleurs en options groupées pour un <Select>
 * d'Ant Design (`{ label, options: [...] }`).
 *
 * @param {Array} couleurs  ce que rend GET /api/produit/couleur
 * @param {object} opts
 *   - avecNbNuances : ajouter « (n) » au titre du groupe (utile en filtre,
 *     inutile en saisie où le compte ne veut rien dire).
 * @returns {Array} options groupées, ou options plates si aucune famille connue.
 */
export function optionsCouleursParFamille(couleurs, { avecNbNuances = false } = {}) {
  const liste = Array.isArray(couleurs) ? couleurs : [];
  if (!liste.length) return [];

  const option = (c) => ({
    value: c.id_couleur,
    // `label` sert AUSSI au filtrage de la saisie (optionFilterProp="label") :
    // il doit rester le nom seul, sinon taper « bordeau » ne trouverait rien.
    label: c.description,
  });

  // Aucune famille connue : menu plat, exactement comme avant. C'est le repli
  // qui protège les APK déjà distribuées, qui parlent à un serveur plus récent
  // mais peuvent aussi tomber sur un plus ancien.
  const aDesFamilles = liste.some((c) => c && c.famille);
  if (!aDesFamilles) return liste.map(option);

  const groupes = new Map();
  liste.forEach((c) => {
    const nom = (c && c.famille) || SANS_FAMILLE;
    // La position vient du serveur : les familles les plus portées d'abord
    // (« Noir » pèse 495 variantes, il n'a rien à faire en bas de liste).
    // Sans famille = toujours en dernier.
    const position = c && c.famille ? Number(c.famille_position ?? 500) : 9999;
    if (!groupes.has(nom)) groupes.set(nom, { nom, position, enfants: [] });
    groupes.get(nom).enfants.push(option(c));
  });

  return [...groupes.values()]
    .sort((a, b) => (a.position - b.position) || a.nom.localeCompare(b.nom, 'fr'))
    .map((g) => ({
      label: avecNbNuances && g.enfants.length > 1 ? `${g.nom} (${g.enfants.length})` : g.nom,
      options: g.enfants.slice().sort((a, b) => String(a.label).localeCompare(String(b.label), 'fr')),
    }));
}

/**
 * Les familles seules, pour un filtre « par famille » (un choix au lieu de 55).
 * La valeur est le NOM de la famille : c'est ce que l'API renvoie sur chaque
 * couleur, donc le filtrage se fait côté écran sans appel supplémentaire.
 */
export function optionsFamilles(couleurs) {
  const liste = Array.isArray(couleurs) ? couleurs : [];
  const vues = new Map();
  liste.forEach((c) => {
    if (!c || !c.famille) return;
    if (!vues.has(c.famille)) vues.set(c.famille, { nom: c.famille, position: Number(c.famille_position ?? 500), n: 0 });
    vues.get(c.famille).n += 1;
  });
  return [...vues.values()]
    .sort((a, b) => (a.position - b.position) || a.nom.localeCompare(b.nom, 'fr'))
    .map((f) => ({ value: f.nom, label: `${f.nom} (${f.n})` }));
}

/** Les identifiants de couleur d'une famille — pour filtrer une liste déjà chargée. */
export function idsDeLaFamille(couleurs, nomFamille) {
  if (!nomFamille) return null;
  return new Set((Array.isArray(couleurs) ? couleurs : [])
    .filter((c) => c && c.famille === nomFamille)
    .map((c) => c.id_couleur));
}
